import React, {
  useState,
  useEffect,
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
} from 'react';
import { useUpdateEffect } from 'react-use';
import {
  Box,
  Flex,
  Icon,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Kbd,
  InputProps,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';

import parsePhoneNumber, { AsYouType, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

import { countries } from '@olo-web/utils/common/constants';
import { ChevronDown, Phone } from 'react-feather';

const Flag = dynamic(() => import('react-world-flags'));

interface PhoneNumberInputProps extends Omit<InputProps, 'onChange'> {
  country?: CountryCode;
  onChange: (phoneNumber: string) => void;
  pl?: number;
  type: string;
  placeholder: string;
  name: string;
  value: string;
  [rest: string]: any;
}

const PhoneNumberInputRenderer: ForwardRefRenderFunction<
  HTMLInputElement,
  PhoneNumberInputProps
> = (props, ref) => {
  const {
    country = 'US',
    onChange,
    pl = 20,
    type = 'tel',
    placeholder = '555 555 5555',
    name = 'phone',
    size = 'md',
    value = '',
    enabledSelectCountry = true,
    ...rest
  } = props;

  const [number, setNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(country);
  const [alreadyFilledValue, setAlreadyFilledValue] = useState(false);
  const [_, setCountryCode] = useState(getCountryCallingCode(country));
  const { isOpen: isFocused, onToggle } = useDisclosure();

  useEffect(() => {
    setSelectedCountry(country);
    setCountryCode(getCountryCallingCode(country));
  }, [country]);

  const onCountryChange = (e) => {
    const cntry = e.target.value;
    const code = getCountryCallingCode(cntry);

    setCountryCode(code);
    setSelectedCountry(cntry);
    onChange(cntry);
  };

  const onPhoneNumberChange = useCallback(
    async (e) => {
      const phone = e.target.value;
      const parsed = await parsePhoneNumber(phone, selectedCountry);

      setNumber(phone);
      const p = parsed ? parsed.formatInternational(selectedCountry as any) : '';
      onChange(p.split(' ').join('').trim());
    },
    [onChange, selectedCountry]
  );

  useUpdateEffect(() => {
    if (value && !alreadyFilledValue && !number) {
      onPhoneNumberChange({ target: { value } });
      setAlreadyFilledValue(true);
    }
  }, [value]);
  // https://github.com/catamphetamine/libphonenumber-js/issues/225#issuecomment-404514300
  const isMoreThenThreeDigits = /(.?\d){4,}/.test(number);

  const currentValue = isMoreThenThreeDigits
    ? new AsYouType(selectedCountry).input(number)
    : number;

  return (
    <InputGroup size={size}>
      {enabledSelectCountry && (
        <InputLeftElement width="4rem">
          <Select
            aria-label="Choose Country Code"
            top="0"
            left="0"
            zIndex={1}
            bottom={0}
            opacity={0}
            height="100%"
            position="absolute"
            value={selectedCountry}
            onChange={onCountryChange}
            iconSize={'xs'}
            iconColor="transparent"
            onFocus={onToggle}
            onBlur={onToggle}
          >
            {countries.map((option) => (
              <option key={option.code} value={option.code}>
                {option?.name}
              </option>
            ))}
          </Select>
          <Flex pl={2} width="100%" alignItems="center">
            {selectedCountry ? (
              <Box mr={2} width="50%" flex={1}>
                <Flag
                  alt="country flag"
                  aria-hidden="true"
                  height="1rem"
                  code={selectedCountry || ''}
                />
              </Box>
            ) : (
              <Icon as={Phone} />
            )}
            {isFocused ? (
              <Kbd aria-label="use down arrow to select country phone code">↓</Kbd>
            ) : (
              <Icon as={ChevronDown} />
            )}
          </Flex>
        </InputLeftElement>
      )}
      <Input
        focusBorderColor="primary.200"
        _hover={{ borderColor: 'blackAlpha.200' }}
        pl={enabledSelectCountry && pl}
        name={name}
        type={type}
        onChange={onPhoneNumberChange}
        placeholder={placeholder}
        {...rest}
        value={currentValue}
        ref={ref}
        _focus={{
          borderColor: 'primary.400',
        }}
      />
    </InputGroup>
  );
};

export const PhoneNumberInput = forwardRef(PhoneNumberInputRenderer);
